import { FC } from 'react';
import {
  DangerButton,
  Icon,
  Button,
  useTranslation,
  DateTimeFormat,
  ToolTip,
} from '@testsigma/atoms';
import { TestCaseResult } from '@testsigmainc/models';

import DeleteModal from '@modules/test-development/components/delete-modal';
import { System } from '@models/system.model';
import { DrifterService } from '@modules/test-development/services/drifter.service';

interface ItemDetailProps {
  title: string;
  children?: React.ReactNode;
  iconName?: string;
  content?: string;
}
/* eslint-disable @typescript-eslint/no-unused-vars */
const ItemDetail: FC<ItemDetailProps> = ({
  title,
  content,
  iconName,
  children,
}) => {
  /* eslint-enable @typescript-eslint/no-unused-vars */
  return (
    <div className='flex flex-col gap-y-2'>
      <div className=' text-2xs text-secondary-text leading-4'>{title}</div>
      {children && (
        <div className='text-xs font-medium text-secondary-1000'>
          {children}
        </div>
      )}
    </div>
  );
};

interface DebuggerStopStartModalProps {
  show?: boolean;
  onClose?: (isKilled?: boolean) => void;
  sessionDetails?: any;
  onStop?: () => void;
  isStopping?: boolean;
  agent?: System;
  testCaseResult?: TestCaseResult;
  stopOnly?: boolean;
}

const DebuggerStopStartModal: FC<DebuggerStopStartModalProps> = ({
  show,
  onClose: onCloseHandler,
  sessionDetails,
  onStop: onStopHandler,
  isStopping,
  agent,
  testCaseResult,
  stopOnly,
}) => {
  const { t } = useTranslation();

  const onStop = () => {
    onStopHandler();
    // removed modified steps from local storage for mobile debugger session
    localStorage.removeItem('modifiedSteps');
    DrifterService.stopMobileSession().catch((error) => {
      // eslint-disable-next-line no-console
      console.error('Failed to clear mobile information:', error);
    });
  };

  return (
    <DeleteModal
      show={show}
      onHideModal={onCloseHandler}
      contentDirection='col'
      className='!w-150'
      showCloseIcon={true}
    >
      <div className='flex flex-col gap-y-6 pt-3'>
        <div className='flex flex-col gap-y-2'>
          <div className=' text-secondary-unique-800 text-lg font-semibold'>
            {t('debugging_session.detected.heading')}
          </div>
          <div className=' text-xs leading-5 text-secondary-unique-800'>
            {t('debugging_session.detected.content')}
          </div>
        </div>
        <div className=' border-0.5 border-secondary-200 rounded-lg p-4 grid grid-cols-2 gap-x-4 gap-y-6'>
          {testCaseResult?.testCaseDetails?.name && (
            <ItemDetail title={t('usage_details.table_col.test_case_name')}>
              <div className='text-xs font-medium text-secondary-1000'>
                {testCaseResult?.testCaseDetails?.name}
              </div>
            </ItemDetail>
          )}

          <ItemDetail title={t('usage_details.table_col.agent_name')}>
            <div className='flex items-center gap-x-2 text-xs font-medium text-secondary-1000 min-w-0'>
              <Icon 
                name={'desktop-windows'}
                className=' text-xl leading-5 text-secondary-unique-800 shrink-0'
              ></Icon>
              <span className='truncate'>
                <ToolTip content={agent?.title}>{agent?.title ?? '—'}</ToolTip>
              </span>
            </div>
          </ItemDetail>
          {sessionDetails?.startTime && (
            <ItemDetail
              title={t('debugging_session.detected_popup.started_on')}
            >
              <DateTimeFormat
                date={sessionDetails?.startTime}
                format={'MMM d, yyyy, hh:mm a'}
                className='text-xs font-medium text-secondary-1000'
                relative={false}
                
              />
            </ItemDetail>
          )}
          <ItemDetail title={t('usage_details.table_col.test_case_location')}>
            {sessionDetails?.executionResult?.execution?.applicationVersion
              ?.application?.project?.name && (
              <div className='basis-full truncate'>
                <span>
                  <ToolTip
                    content={t('debugging_session.testcase_location.project', {
                      projectName:
                        sessionDetails?.executionResult?.execution
                          ?.applicationVersion?.application?.project?.name,
                    })}
                  >
                    {
                      sessionDetails?.executionResult?.execution
                        ?.applicationVersion?.application?.project?.name
                    }
                  </ToolTip>
                </span>
              </div>
            )}

            {sessionDetails?.executionResult?.execution?.applicationVersion
              ?.application?.name && (
              <div className='basis-full truncate'>
                <span>
                  <ToolTip
                    content={t('debugging_session.testcase_location.app', {
                      applicationName:
                        sessionDetails?.executionResult?.execution
                          ?.applicationVersion?.application?.name,
                    })}
                  >
                    {
                      sessionDetails?.executionResult?.execution
                        ?.applicationVersion?.application?.name
                    }
                  </ToolTip>
                </span>
              </div>
            )}
          </ItemDetail>
        </div>
        <div className='flex justify-end gap-x-3 mt-6'>
          <Button type='light' onClick={onCloseHandler}>
            {t('common.cancel')}
          </Button>
          <DangerButton
            isDisabled={isStopping}
            className=' gap-x-1.5'
            onClick={onStop}
          >
            <Icon name='stop-circle-outline' className=' text-lg'></Icon>
            {stopOnly
              ? t('debugging_session.detected_popup.actions.stop')
              : t(
                  'debugging_session.detected_popup.actions.stop_and_start_new',
                )}
          </DangerButton>
        </div>
      </div>
    </DeleteModal>
  );
};

export default DebuggerStopStartModal;
